import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { DataTable, type Column } from "@/components/v2/DataTable";
import { EmptyState } from "@/components/v2/EmptyState";

type Row = {
  id?: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  lifetimeValue?: number;
  firstSeenAt?: string;
};

/**
 * Service Detail · Clients tab — Pencil `prh1l`. Distinct customers who
 * booked this service, paged. Backend filter `?serviceId=` does the join
 * via `EXISTS (SELECT 1 FROM Booking b WHERE b.customerId = c.id AND
 * b.serviceId = :serviceId)`.
 */
export function ServiceClients() {
  const { id } = useParams<{ id: string }>();
  const accessToken = useAuthStore((s) => s.accessToken);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const list = useQuery({
    queryKey: ["customers", "service", id, page, pageSize],
    enabled: !!accessToken && !!id,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/customers", {
          params: { query: { serviceId: id, page, size: pageSize } },
        }),
      ),
  });

  const cols: Column<Row>[] = [
    {
      key: "client",
      header: "Client",
      cell: (r) => {
        const name =
          [r.firstName, r.lastName].filter(Boolean).join(" ") || r.email || "—";
        const initials = name
          .split(" ")
          .map((p) => p[0]?.toUpperCase() ?? "")
          .slice(0, 2)
          .join("");
        return (
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 rounded-full bg-primary/10 text-primary inline-flex items-center justify-center text-[11px] font-medium shrink-0">
              {initials || "—"}
            </div>
            <div className="min-w-0">
              <p className="font-medium text-sm truncate">{name}</p>
              <p className="text-xs text-muted-foreground truncate">{r.email ?? "—"}</p>
            </div>
          </div>
        );
      },
    },
    { key: "phone", header: "Phone", cell: (r) => r.phone ?? "—" },
    {
      key: "ltv",
      header: "Lifetime value",
      align: "right",
      cell: (r) => (
        <span className="font-medium tabular-nums">
          {Number(r.lifetimeValue ?? 0).toLocaleString()}
        </span>
      ),
    },
    {
      key: "first",
      header: "First seen",
      cell: (r) => (
        <span className="text-xs text-muted-foreground">
          {r.firstSeenAt ? new Date(r.firstSeenAt).toLocaleDateString() : "—"}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {list.isError && (
        <p className="text-sm text-destructive">{(list.error as Error).message}</p>
      )}

      {list.data && (
        <DataTable
          columns={cols}
          rows={(list.data.content ?? []) as Row[]}
          rowKey={(r) => r.id ?? Math.random().toString()}
          loading={list.isLoading}
          sortable
          resizable
          hideable
          height={500}
          pagination={{
            pageIndex: page,
            pageSize,
            pageCount: list.data.totalPages ?? 1,
            totalRows: list.data.totalElements,
            onPageChange: setPage,
            onPageSizeChange: (n) => {
              setPageSize(n);
              setPage(0);
            },
          }}
          emptyState={
            <div className="p-8">
              <EmptyState
                title="No clients yet"
                description="Customers who book this service will be listed here."
              />
            </div>
          }
        />
      )}
    </div>
  );
}
